import { useLocation, useNavigate } from 'react-router-dom';
import { Search, Bell, LogOut, Settings } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Avatar, AvatarFallback } from '@/components/ui/Avatar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/Popover';
import { useAuth } from '@/contexts/AuthContext';
import { useWorkspace } from '@/contexts/WorkspaceContext';
import { toast } from 'sonner';
import { WorkspaceSelector } from './WorkspaceSelector';
import { useLogout } from './useLogout';

interface HeaderProps {
  onAddDaily?: () => void;
}

function getInitials(name: string) {
  return name
    .split(' ')
    .map((part) => part[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();
}

export function Header({ onAddDaily }: HeaderProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { workspaces, activeWorkspaceId, switchWorkspace } = useWorkspace();
  const handleLogout = useLogout();
  const userName = user?.name ?? 'User';

  const handleWorkspaceSwitch = async (workspaceId: string) => {
    try {
      await switchWorkspace(workspaceId);

      if (location.pathname.startsWith('/workspace/')) {
        navigate('/workspace/' + workspaceId + '/settings/members', { replace: true });
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not switch workspace');
    }
  };

  const handleAddDaily = () => {
    if (onAddDaily) {
      onAddDaily();
    } else {
      navigate('/dashboard');
    }
  };

  return (
    <header className="sticky top-0 z-30 flex h-16 items-center justify-between border-b bg-background/95 px-4 backdrop-blur sm:px-6">
      <div className="flex min-w-0 items-center gap-2 sm:gap-3">
        <div className="min-w-0 max-w-[min(14rem,calc(100vw-8rem))] lg:hidden">
          <WorkspaceSelector
            workspaces={workspaces}
            activeWorkspaceId={activeWorkspaceId}
            onSwitch={handleWorkspaceSwitch}
            variant="header"
          />
        </div>
        <div className="relative hidden sm:block">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            type="search"
            placeholder="Search anything"
            className="w-64 pl-9 pr-16 lg:w-80"
          />
          <div className="absolute right-2 top-1/2 hidden -translate-y-1/2 items-center gap-1 rounded border px-1.5 py-0.5 text-xs text-muted-foreground sm:flex">
            <span>⌘</span>
            <span>F</span>
          </div>
        </div>
      </div>

      <div className="flex items-center gap-2 sm:gap-3">
        <button
          type="button"
          aria-label="Open notifications"
          className="relative rounded-full p-2 text-muted-foreground hover:bg-muted"
        >
          <Bell className="h-5 w-5" />
        </button>

        <Popover>
          <PopoverTrigger asChild>
            <button
              data-testid="user-menu-trigger"
              aria-label={`Open user menu for ${userName}`}
              className="flex items-center gap-2 rounded-full pl-1 pr-2 py-1 transition-colors hover:bg-muted"
            >
              <Avatar className="h-8 w-8">
                <AvatarFallback className="bg-primary/20 text-xs text-primary">
                  {getInitials(userName)}
                </AvatarFallback>
              </Avatar>
              <span className="hidden text-sm font-medium lg:block">{userName}</span>
            </button>
          </PopoverTrigger>
          <PopoverContent align="end" className="w-56 p-2">
            <div className="flex items-center gap-3 border-b px-2 pb-2">
              <Avatar className="h-9 w-9">
                <AvatarFallback className="bg-primary/20 text-xs text-primary">
                  {getInitials(userName)}
                </AvatarFallback>
              </Avatar>
              <div className="flex flex-col overflow-hidden">
                <span className="truncate text-sm font-medium">{userName}</span>
                <span className="truncate text-xs text-muted-foreground">{user?.email}</span>
              </div>
            </div>
            <button
              type="button"
              onClick={() => navigate('/settings/account')}
              className="mt-1 flex w-full items-center gap-2 rounded-md px-2 py-2 text-sm font-medium transition-colors hover:bg-muted"
            >
              <Settings className="h-4 w-4" />
              Account settings
            </button>
            <div className="my-1 border-t" />
            <a
              href="https://daysheetly.com/privacy"
              className="flex w-full items-center rounded-md px-2 py-2 text-sm text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            >
              Privacy policy
            </a>
            <a
              href="https://daysheetly.com/terms"
              className="flex w-full items-center rounded-md px-2 py-2 text-sm text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            >
              Terms of service
            </a>
            <button
              onClick={handleLogout}
              className="mt-1 flex w-full items-center gap-2 rounded-md px-2 py-2 text-sm font-medium text-destructive transition-colors hover:bg-destructive/10"
            >
              <LogOut className="h-4 w-4" />
              Log out
            </button>
          </PopoverContent>
        </Popover>
      </div>
    </header>
  );
}
